import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryParam, StringParam } from 'use-query-params';

import { getItemsInPickingArea, getPickingAreas } from 'apis/picker-api/api';
import { PickingArea } from 'apis/picker-api/types';
import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined, localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import useMenuData from 'modules/menu/useMenuData';

import { UNASSIGNED_AREA_ID } from '../constants';
import { ListElement } from '../types';

export type PickingState = 'no-menu' | 'loading-single-area' | 'loading' | 'settled' | 'error' | 'idle';

const sortByOrder = <T1 extends { order: number }, T2 extends { order: number }>(el1: T1, el2: T2) =>
  el1.order - el2.order;

const usePickingAreas = (venueId: string, menuId: string | null) => {
  const intl = useIntl();

  const { primaryLanguage } = useContext(MenuLocaleContext);

  const [activeAreaId, setActiveAreaId] = useQueryParam('areaId', StringParam);

  const [activeAreaItems, setActiveAreaItems] = useState<ListElement[]>([]);
  const [pickingAreas, setPickingAreas] = useState<PickingArea[]>();

  useEffect(() => {
    if (activeAreaId === undefined) {
      setActiveAreaId(UNASSIGNED_AREA_ID);
    }
  }, [activeAreaId, setActiveAreaId]);

  const {
    isFetching: isFetchingAreas,
    error: areasError,
    refetch: refetchPickingAreas,
  } = useQuery(['get-picking-areas', venueId], () => getPickingAreas(venueId), {
    enabled: !!menuId,
    refetchOnWindowFocus: false,
    onSuccess: data => setPickingAreas(data.sort(sortByOrder)),
  });

  const { loadingMenuData: isFetchingMenu, menu, items, menuDataError: menuError } = useMenuData();

  const buildPickingAreaItemsFromMenu = useCallback(
    (pickingAreaItemIds: string[]): ListElement[] => {
      if (!menu || !activeAreaId) {
        return [];
      }
      if (activeAreaId !== UNASSIGNED_AREA_ID) {
        return pickingAreaItemIds.reduce((acc, itemId) => {
          const item = items.find(item => item.id === itemId);
          return !item ? acc : [...acc, { type: 'item', data: item, areaId: activeAreaId }];
        }, []);
      }
      const categoryIndexedItems = menu.categories.reduce<Record<string, Item[]>>((acc, category) => {
        const itemBindings = [
          ...category.item_bindings,
          ...category.child_categories.flatMap(subcategory => subcategory.item_bindings),
        ]
          .filter(binding => pickingAreaItemIds.includes(binding.item_id))
          .map(binding => items.find(item => item.id === binding.item_id))
          .filter(isDefined);
        if (itemBindings.length === 0) {
          return acc;
        }
        return { ...acc, [category.id]: itemBindings };
      }, {});

      const categoryItemIds = Object.values(categoryIndexedItems).flatMap(items => items.map(item => item.id));
      const itemsWithoutCategory = items.filter(
        item => !categoryItemIds.includes(item.id) && pickingAreaItemIds.includes(item.id),
      );

      if (itemsWithoutCategory.length > 0) {
        categoryIndexedItems.noCategory = itemsWithoutCategory;
      }

      return Object.entries(categoryIndexedItems).flatMap(([categoryId, items]) => {
        if (categoryId === 'noCategory') {
          return [
            {
              type: 'header' as const,
              data: {
                id: categoryId,
                name: intl.formatMessage({ id: 'menu.nonCategorised' }),
                itemIds: items.map(item => item.id),
              },
            },
            ...items.map(item => ({ type: 'item' as const, data: item, areaId: UNASSIGNED_AREA_ID })),
          ];
        }
        const categoryName = menu.categories.find(category => category.id === categoryId)?.name || [];
        return [
          {
            type: 'header' as const,
            data: {
              id: categoryId,
              name: localize(categoryName, primaryLanguage),
              itemIds: items.map(item => item.id),
            },
          },
          ...items.map(item => ({ type: 'item' as const, data: item, areaId: UNASSIGNED_AREA_ID })),
        ];
      });
    },
    [activeAreaId, intl, items, menu, primaryLanguage],
  );

  const { isFetching: isFetchingSingleArea, refetch: refetchActiveArea } = useQuery(
    ['get-items-in-picking-area', activeAreaId],
    async () => {
      if (!activeAreaId) {
        return [];
      }
      return getItemsInPickingArea(venueId, activeAreaId);
    },
    {
      enabled: !!activeAreaId && !!menu,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        const pickingAreaItemIds =
          activeAreaId === UNASSIGNED_AREA_ID
            ? data.map(item => item.id)
            : [...data].sort(sortByOrder).map(item => item.id);
        const itemsWithMenuData = buildPickingAreaItemsFromMenu(pickingAreaItemIds);
        setActiveAreaItems(itemsWithMenuData);
      },
    },
  );

  const error = menuError || areasError;

  const state = useMemo<PickingState>(() => {
    if (error) {
      return 'error';
    }
    if (!menuId) {
      return 'no-menu';
    }
    if (isFetchingSingleArea) {
      return 'loading-single-area';
    }
    if ((!pickingAreas && isFetchingAreas) || (!menu && isFetchingMenu)) {
      return 'loading';
    }
    if (!error && !!pickingAreas && !!menu) {
      return 'settled';
    }
    return 'idle';
  }, [error, isFetchingAreas, isFetchingSingleArea, isFetchingMenu, menu, menuId, pickingAreas]);

  return {
    menuItems: items,
    activeAreaId: activeAreaId || undefined,
    setActiveAreaId,
    activeAreaItems,
    setActiveAreaItems,
    pickingAreas,
    setPickingAreas,
    state,
    error,
    refetchPickingAreas,
    refetchActiveArea,
  };
};

export default usePickingAreas;
